import React, { useEffect, useRef } from "react";

import { scroll, timeline } from "motion";

import { vars } from "~styles/themes/theme.css";

import {
  ANIMATED,
  getElemsByMotionAttr,
} from "~utils/motion_one/motion_attribute_selectors";

import {
  type BentoItemProps,
  BentoStatItem,
} from "~components/BentoGridStats/BentoStatItem";
import { getBentoGridStyle } from "~components/BentoGridStats/styles.css";
import { Box } from "~components/Box";

import type { MotionTimelineDefinition } from "~animations/products_addons/types";
import type { VariantBackgroundColorEnum } from "~styles/common/variant.backgroundColor.css";

export interface BentoGridProps {
  desktopColumns: "2" | "3" | "4";
  content: BentoItemProps[];
  statsBackground?: VariantBackgroundColorEnum;
  isAnimated?: boolean;
}

export function BentoGridStats({
  content,
  desktopColumns,
  statsBackground = "background_charcoal",
  isAnimated = true,
}: BentoGridProps) {
  const refContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Exit if we're on the server, the component isn't mounted, or animation is disabled
    if (typeof window === "undefined" || !refContainer.current || !isAnimated) {
      return;
    }

    // Find all elements within the container marked for animation
    const allAnimatedElements = getElemsByMotionAttr(refContainer, "primary");

    if (allAnimatedElements.length > 0) {
      // Create a staggered animation sequence for all found elements
      const sequence: MotionTimelineDefinition = Array.from(
        allAnimatedElements
      ).map((element, index) => {
        const animOffset = index / allAnimatedElements.length; // Stagger start time

        return [
          element,
          {
            opacity: [0, 1],
            scale: [0.8, 1],
            y: [vars.spacing.spacing5, 0], // Animate from a lower position
          },
          {
            at: animOffset, // Apply the stagger
          },
        ];
      });

      // Tie the animation timeline to the scroll position
      scroll(timeline(sequence), {
        target: refContainer.current,
        offset: ["start 0.75", "end 0.75"], // Start when top of element hits bottom of viewport, finish when centers align
      });
    }
  }, [isAnimated, content]); // Rerun effect if animation is toggled or content changes

  return (
    <Box
      display="grid"
      justifyItems="stretch"
      alignItems="stretch"
      ref={refContainer}
      className={getBentoGridStyle({
        desktop: desktopColumns,
        tablet: "2",
        mobile: "2",
      })}
    >
      {content.map((item, index) => {
        return (
          <BentoStatItem
            // Using a unique ID from the data is best, fallback to index
            key={item._uid || index}
            {...item}
            background={statsBackground}
            data-motion={ANIMATED.primary}
            // Set initial opacity to 0 if animated, so it can be faded in
            opacity={isAnimated ? "0" : "1"}
          />
        );
      })}
    </Box>
  );
}
